import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import './PurchaseOrderForm.css';

const PurchaseOrderForm = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { poId } = useParams();

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Vendor autocomplete state
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [isSearchingVendors, setIsSearchingVendors] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const vendorInputRef = useRef(null);
  const vendorDropdownRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    job_id: '',
    to_field: '',
    date: new Date().toISOString().split('T')[0],
    how_shipped: 'Will call',
    total_price: 0,
    notes: '',
    line_items: []
  });

  const [job, setJob] = useState(null);

  // Load existing PO for editing
  useEffect(() => {
    if (poId) {
      setIsEditing(true);
      loadPurchaseOrder();
    } else if (location.state?.job) {
      // New PO from job selection
      setJob(location.state.job);
      setFormData(prev => ({
        ...prev,
        job_id: location.state.job.id
      }));
    } else {
      toast.error('No job selected');
      navigate('/purchase-orders');
    }
  }, [poId]);

  // Vendor search with debounce
  useEffect(() => {
    if (!vendorSearch.trim()) {
      setVendorSuggestions([]);
      setShowVendorDropdown(false);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsSearchingVendors(true);
      try {
        const response = await api.get('/vendors', {
          params: { search: vendorSearch.trim() }
        });
        const vendors = response.data || [];
        setVendorSuggestions(vendors);
        setShowVendorDropdown(vendors.length > 0);
        setHighlightedIndex(-1);
      } catch (error) {
        console.error('Error searching vendors:', error);
        setVendorSuggestions([]);
      } finally {
        setIsSearchingVendors(false);
      }
    }, 200);

    return () => clearTimeout(debounceTimer);
  }, [vendorSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target) &&
          vendorInputRef.current && !vendorInputRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle vendor selection
  const handleSelectVendor = useCallback((vendor) => {
    setFormData(prev => ({ ...prev, to_field: vendor.name }));
    setVendorSearch('');
    setShowVendorDropdown(false);
    setVendorSuggestions([]);
  }, []);

  // Handle vendor input change
  const handleVendorInputChange = (e) => {
    const value = e.target.value;
    setVendorSearch(value);
    setFormData(prev => ({ ...prev, to_field: value }));
  };

  // Handle keyboard navigation for vendor dropdown
  const handleVendorKeyDown = (e) => {
    if (!showVendorDropdown && vendorSuggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < vendorSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        // If only one suggestion, select it
        if (vendorSuggestions.length === 1) {
          handleSelectVendor(vendorSuggestions[0]);
        } else if (highlightedIndex >= 0 && highlightedIndex < vendorSuggestions.length) {
          // Select highlighted item
          handleSelectVendor(vendorSuggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowVendorDropdown(false);
        break;
      case 'Tab':
        // If only one suggestion, select it on Tab
        if (vendorSuggestions.length === 1) {
          handleSelectVendor(vendorSuggestions[0]);
        }
        setShowVendorDropdown(false);
        break;
      default:
        break;
    }
  };

  const loadPurchaseOrder = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/purchase-orders/${poId}`);
      const po = response.data;

      setFormData({
        job_id: po.job_id,
        to_field: po.to_field,
        date: po.date,
        how_shipped: po.how_shipped,
        total_price: po.total_price,
        notes: po.notes || '',
        line_items: po.line_items || []
      });

      if (location.state?.job) {
        setJob(location.state.job);
      }
    } catch (error) {
      console.error('Error loading purchase order:', error);
      toast.error('Failed to load purchase order');
      navigate('/purchase-orders');
    } finally {
      setIsLoading(false);
    }
  };

  // Add new line item
  const handleAddLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        { qty: 1, description: '', price: null }
      ]
    }));
  };

  // Remove line item
  const handleRemoveLineItem = (index) => {
    setFormData(prev => {
      const newLineItems = prev.line_items.filter((_, i) => i !== index);

      // Auto-calculate total after removing item
      const newTotal = newLineItems.reduce((sum, item) => {
        return sum + (item.price ? parseFloat(item.price) * item.qty : 0);
      }, 0);

      return {
        ...prev,
        line_items: newLineItems,
        total_price: newTotal
      };
    });
  };

  // Update line item
  const handleLineItemChange = (index, field, value) => {
    setFormData(prev => {
      const newLineItems = [...prev.line_items];
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: field === 'qty' ? parseInt(value) || 0 :
                 field === 'price' ? (value === '' ? null : parseFloat(value)) :
                 value
      };

      // Auto-calculate total when line items change
      const newTotal = newLineItems.reduce((sum, item) => {
        return sum + (item.price ? parseFloat(item.price) * item.qty : 0);
      }, 0);

      return {
        ...prev,
        line_items: newLineItems,
        total_price: newTotal
      };
    });
  };

  // Save purchase order
  const handleSave = async () => {
    // Validation
    if (!formData.to_field.trim()) {
      toast.error('Please enter a vendor/supplier name');
      return;
    }

    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    if (!formData.total_price || formData.total_price <= 0) {
      toast.error('Please enter a valid total price');
      return;
    }

    setIsSaving(true);
    try {
      let savedPO;
      if (isEditing) {
        const response = await api.put(`/purchase-orders/${poId}`, formData);
        savedPO = response.data;
        toast.success('Purchase order updated successfully');
      } else {
        const response = await api.post('/purchase-orders', formData);
        savedPO = response.data;
        toast.success('Purchase order created successfully');
      }

      // Auto-open print dialog after creation/update
      if (savedPO && savedPO.id && savedPO.po_number) {
        try {
          // Fetch full PO data
          const poResponse = await api.get(`/purchase-orders/${savedPO.id}`);
          const fullPO = poResponse.data;

          // Generate line items HTML
          const lineItemRows = fullPO.line_items?.map(item => `
            <tr>
              <td style="text-align: center;">${item.qty}</td>
              <td>${item.description}</td>
              <td style="text-align: right;">${item.price ? '$' + parseFloat(item.price).toFixed(2) : ''}</td>
              <td></td>
            </tr>
          `).join('') || '';

          // Add empty rows to fill the form
          const currentRows = fullPO.line_items?.length || 0;
          const minRows = 12;
          const emptyRowsNeeded = Math.max(0, minRows - currentRows);
          const emptyRows = Array(emptyRowsNeeded).fill('<tr><td>&nbsp;</td><td></td><td></td><td></td></tr>').join('');

          // Parse address to split street from city/state/zip
          const fullAddress = fullPO.job?.address || job?.address || '';
          let streetAddress = '';
          let cityStateZip = '';

          // Try to find city/state pattern: "City, ST" or "City, ST ZIP"
          const statePattern = /,\s*([A-Z]{2})(\s+\d{5}(-\d{4})?)?$/;
          const stateMatch = fullAddress.match(statePattern);

          if (stateMatch) {
            // Found state pattern, now find where city starts
            const beforeState = fullAddress.substring(0, stateMatch.index);
            // Look for the last space that's likely the city separator
            // Common patterns: "Street Address City, ST" - city is usually 1-3 words before comma
            const words = beforeState.trim().split(/\s+/);

            // Assume city is last 1-2 words before the state (heuristic)
            // If address has "Blvd", "St", "Ave", "Dr", "Rd", "Ln", "Way", "Ct" etc., city starts after
            const streetSuffixes = ['blvd', 'boulevard', 'st', 'street', 'ave', 'avenue', 'dr', 'drive', 'rd', 'road', 'ln', 'lane', 'way', 'ct', 'court', 'cir', 'circle', 'pl', 'place', 'pkwy', 'parkway', 'hwy', 'highway'];
            let cityStartIndex = words.length - 2; // Default: assume city is last 2 words

            for (let i = words.length - 1; i >= 0; i--) {
              const word = words[i].toLowerCase().replace(/[.,]/g, '');
              if (streetSuffixes.includes(word)) {
                cityStartIndex = i + 1;
                break;
              }
            }

            streetAddress = words.slice(0, cityStartIndex).join(' ');
            const city = words.slice(cityStartIndex).join(' ');
            cityStateZip = city + fullAddress.substring(stateMatch.index);
          } else {
            // Fallback to comma split
            const addressParts = fullAddress.split(',').map(part => part.trim());
            streetAddress = addressParts[0] || '';
            cityStateZip = addressParts.slice(1).join(', ').trim();
          }

          // Shop address split
          const shopStreet = '10161 N. Blanchard St. - Suite A';
          const shopCityStateZip = 'Riverside, CA 92509';

          // Create print window
          const printWindow = window.open('', '_blank');
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>Purchase Order ${fullPO.po_number}</title>
              <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: Arial, sans-serif; padding: 30px; background: #fff; color: #333; }

                .action-buttons {
                  display: flex;
                  gap: 10px;
                  margin-bottom: 20px;
                  justify-content: center;
                }
                .action-buttons button {
                  padding: 10px 30px;
                  font-size: 16px;
                  font-weight: bold;
                  border: none;
                  border-radius: 5px;
                  cursor: pointer;
                  transition: background-color 0.2s;
                }
                .btn-print {
                  background-color: #007bff;
                  color: white;
                }
                .btn-print:hover {
                  background-color: #0056b3;
                }
                .btn-cancel {
                  background-color: #6c757d;
                  color: white;
                }
                .btn-cancel:hover {
                  background-color: #545b62;
                }

                .po-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000; padding-bottom: 10px; margin-bottom: 8px; }
                .po-company-name { font-size: 28px; font-weight: bold; color: #000; line-height: 1.1; }
                .po-company-subtitle { font-size: 18px; font-weight: bold; color: #000; }
                .po-title { text-align: right; }
                .po-title-text { font-size: 20px; font-weight: bold; }
                .po-number { font-size: 14px; margin-top: 5px; }
                .po-company-info { font-size: 9px; margin: 8px 0 20px 0; line-height: 1.5; }
                .po-info-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                .po-info-table td { padding: 5px 8px; border: 1px solid #000; font-size: 10px; }
                .po-info-table td:nth-child(odd) { font-weight: bold; width: 20%; background: #fff; }
                .po-info-table td:nth-child(even) { width: 30%; }
                .po-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                .po-table th { background: #e0e0e0; font-weight: bold; padding: 6px 8px; border: 1px solid #000; text-align: left; font-size: 10px; }
                .po-table td { padding: 5px 8px; border: 1px solid #000; font-size: 10px; }
                .po-total-row td { font-weight: bold; border-top: 2px solid #000; }
                .po-footer { display: flex; gap: 0; }
                .po-footer-box { flex: 1; border: 1px solid #000; padding: 12px; font-size: 9px; line-height: 1.5; }
                .po-footer-title { font-weight: bold; margin-bottom: 8px; }

                @media print {
                  .action-buttons { display: none; }
                  body { padding: 20px; }
                  @page { margin: 0.5in; size: letter; }
                }
              </style>
            </head>
            <body>
              <div class="action-buttons">
                <button class="btn-print" onclick="window.print()">Print</button>
                <button class="btn-cancel" onclick="window.close()">Cancel</button>
              </div>

              <div class="po-header">
                <div>
                  <div class="po-company-name">SCOTT</div>
                  <div class="po-company-subtitle">OVERHEAD DOOR</div>
                </div>
                <div class="po-title">
                  <div class="po-title-text">PURCHASE ORDER</div>
                  <div class="po-number">N° ${fullPO.po_number}</div>
                </div>
              </div>

              <div class="po-company-info">
                10161 N. Blanchard St. - Suite A<br/>
                Riverside, CA 92509<br/>
                T: (714) 636-9318 - F: (714) 636-9073<br/>
                Lic. # 872640
              </div>

              <table class="po-info-table">
                <tr>
                  <td>TO:</td>
                  <td>${fullPO.to_field || ''}</td>
                  <td>DATE:</td>
                  <td>${fullPO.date ? new Date(fullPO.date).toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: '2-digit'}) : ''}</td>
                </tr>
                <tr>
                  <td>CITY, STATE, ZIP:</td>
                  <td></td>
                  <td>TERMS:</td>
                  <td></td>
                </tr>
                <tr>
                  <td>SHIP TO:</td>
                  <td>${fullPO.how_shipped === 'To jobsite' ? (fullPO.job?.customer_name || job?.customer_name || '') : (fullPO.how_shipped === 'To Shop' ? 'Scott Overhead Door' : fullPO.to_field || '')}</td>
                  <td>HOW SHIPPED:</td>
                  <td>${fullPO.how_shipped || ''}</td>
                </tr>
                <tr>
                  <td>ADDRESS:</td>
                  <td>${fullPO.how_shipped === 'To jobsite' ? streetAddress : (fullPO.how_shipped === 'To Shop' ? shopStreet : '')}</td>
                  <td>F.O.B.:</td>
                  <td></td>
                </tr>
                <tr>
                  <td>CITY, STATE, ZIP:</td>
                  <td>${fullPO.how_shipped === 'To jobsite' ? cityStateZip : (fullPO.how_shipped === 'To Shop' ? shopCityStateZip : '')}</td>
                  <td>P.O. #:</td>
                  <td>${fullPO.po_number || ''}</td>
                </tr>
              </table>

              <table class="po-table">
                <thead>
                  <tr>
                    <th style="width: 80px; text-align: center;">QUANTITY</th>
                    <th>DESCRIPTION</th>
                    <th style="width: 100px; text-align: right;">PRICE</th>
                    <th style="width: 80px; text-align: center;">UNIT</th>
                  </tr>
                </thead>
                <tbody>
                  ${lineItemRows}
                  ${emptyRows}
                  <tr class="po-total-row">
                    <td></td>
                    <td style="text-align: right; font-weight: bold;">Total</td>
                    <td style="text-align: right; font-weight: bold;">$${parseFloat(fullPO.total_price || 0).toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>

              <div class="po-footer">
                <div class="po-footer-box">
                  ${fullPO.notes ? `<div class="po-footer-title">NOTES:</div><div>${fullPO.notes}</div>` : `
                  <div class="po-footer-title">IMPORTANT</div>
                  <div>
                    PURCHASE ORDER MUST APPEAR<br/>
                    ON ALL INVOICES, PACKAGES, BILLS OF<br/>
                    LADING, NOTICES OF DELIVERIES. IF YOU<br/>
                    ARE UNABLE TO COMPLETE ORDER BY<br/>
                    DATE SPECIFIED
                  </div>
                  `}
                </div>
                <div class="po-footer-box">
                  PLEASE SEND _____ COPIES OF YOUR<br/>
                  INVOICE WITH ORIGINAL BILL OF LADING.<br/><br/>
                  PURCHASER AGENT:
                </div>
              </div>
            </body>
            </html>
          `);
          printWindow.document.close();
        } catch (pdfError) {
          console.error('Error opening print dialog:', pdfError);
          toast.warning('Purchase order saved but print dialog failed to open');
        }
      }

      // Don't auto-navigate - let user close print dialog and review/edit if needed
      // User can click "Back to Purchase Orders" button when ready
      // navigate('/purchase-orders');
    } catch (error) {
      console.error('Error saving purchase order:', error);
      toast.error('Failed to save purchase order');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete purchase order
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this purchase order? This action cannot be undone.')) {
      return;
    }

    setIsSaving(true);
    try {
      await api.delete(`/purchase-orders/${poId}`);
      toast.success('Purchase order deleted successfully');
      // Navigate back with job context so user doesn't have to search again
      navigate('/purchase-orders', { state: { job: job || location.state?.job } });
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      toast.error('Failed to delete purchase order');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    // Navigate back with job context so user doesn't have to search again
    navigate('/purchase-orders', { state: { job: job || location.state?.job } });
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-card">
          <div className="loading-spinner"></div>
          <h3 className="loading-title">Loading Purchase Order</h3>
          <p className="loading-subtitle">Please wait...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-order-form-container">
      <button
        className="btn btn-secondary"
        onClick={handleCancel}
        style={{ marginBottom: '15px' }}
      >
        ← Back to Purchase Orders
      </button>
      <div className="page-header">
        <h1>{isEditing ? 'Edit Purchase Order' : 'Create Purchase Order'}</h1>
        {job && <p className="job-info">Job: {job.job_number} - {job.customer_name}</p>}
      </div>

      <div className="form-card card">
        {/* Header Information */}
        <div className="form-section">
          <h3>Purchase Order Details</h3>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>To (Vendor/Supplier) *</label>
              <div className="vendor-autocomplete-container" style={{ position: 'relative' }}>
                <input
                  ref={vendorInputRef}
                  type="text"
                  className="form-control"
                  value={formData.to_field}
                  onChange={handleVendorInputChange}
                  onKeyDown={handleVendorKeyDown}
                  onFocus={() => {
                    if (vendorSuggestions.length > 0) {
                      setShowVendorDropdown(true);
                    }
                  }}
                  placeholder="Type to search vendors..."
                  autoComplete="off"
                />
                {isSearchingVendors && (
                  <div className="vendor-loading" style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px',
                    color: '#6c757d'
                  }}>
                    Searching...
                  </div>
                )}
                {showVendorDropdown && vendorSuggestions.length > 0 && (
                  <div
                    ref={vendorDropdownRef}
                    className="vendor-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: 'white',
                      border: '1px solid #ced4da',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {vendorSuggestions.map((vendor, index) => (
                      <div
                        key={vendor.id}
                        className={`vendor-option ${index === highlightedIndex ? 'highlighted' : ''}`}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: index === highlightedIndex ? '#e9ecef' : 'white',
                          borderBottom: index < vendorSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none'
                        }}
                        onClick={() => handleSelectVendor(vendor)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <div style={{ fontWeight: 500 }}>{vendor.name}</div>
                        {vendor.contact_name && (
                          <div style={{ fontSize: '12px', color: '#6c757d' }}>
                            Contact: {vendor.contact_name}
                          </div>
                        )}
                      </div>
                    ))}
                    {vendorSuggestions.length === 1 && (
                      <div style={{
                        padding: '4px 12px',
                        fontSize: '11px',
                        color: '#6c757d',
                        backgroundColor: '#f8f9fa',
                        borderTop: '1px solid #e9ecef'
                      }}>
                        Press Enter to select
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group col-md-3">
              <label>Date *</label>
              <input
                type="date"
                className="form-control"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>

            <div className="form-group col-md-3">
              <label>How Shipped *</label>
              <select
                className="form-control"
                value={formData.how_shipped}
                onChange={(e) => setFormData({ ...formData, how_shipped: e.target.value })}
              >
                <option value="Will call">Will call</option>
                <option value="To jobsite">To jobsite</option>
                <option value="To Shop">To Shop</option>
              </select>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="form-section">
          <div className="section-header">
            <h3>Line Items</h3>
            <button className="btn btn-sm btn-primary" onClick={handleAddLineItem}>
              + Add Line Item
            </button>
          </div>

          <div className="line-items-table-wrapper">
            {/* Desktop Table View */}
            <table className="table line-items-table">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>Qty</th>
                  <th>Description</th>
                  <th style={{ width: '150px' }}>Price (Optional)</th>
                  <th style={{ width: '80px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {formData.line_items.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-muted">
                      No line items. Click "Add Line Item" to get started.
                    </td>
                  </tr>
                ) : (
                  formData.line_items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.qty}
                          onChange={(e) => handleLineItemChange(index, 'qty', e.target.value)}
                          min="1"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          placeholder="Enter item description"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.price === null ? '' : item.price}
                          onChange={(e) => handleLineItemChange(index, 'price', e.target.value)}
                          placeholder="Optional"
                          step="0.01"
                          min="0"
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveLineItem(index)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="line-items-mobile-cards">
              {formData.line_items.length === 0 ? (
                <div className="text-center text-muted" style={{ padding: '20px' }}>
                  No line items. Click "Add Line Item" to get started.
                </div>
              ) : (
                formData.line_items.map((item, index) => (
                  <div key={index} className="line-item-mobile-card">
                    <div className="card-header">
                      <span className="item-number">Item {index + 1}</span>
                    </div>
                    <div className="form-group">
                      <label>Quantity</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.qty}
                        onChange={(e) => handleLineItemChange(index, 'qty', e.target.value)}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        placeholder="Enter item description"
                      />
                    </div>
                    <div className="form-group">
                      <label>Price (Optional)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.price === null ? '' : item.price}
                        onChange={(e) => handleLineItemChange(index, 'price', e.target.value)}
                        placeholder="Optional"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleRemoveLineItem(index)}
                    >
                      Remove Item
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Total and Notes */}
        <div className="form-section">
          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Notes (Optional)</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any additional notes..."
              />
            </div>

            <div className="form-group col-md-6">
              <label>Total Price *</label>
              <input
                type="number"
                className="form-control total-price-input"
                value={formData.total_price}
                onChange={(e) => setFormData({ ...formData, total_price: parseFloat(e.target.value) || 0 })}
                step="0.01"
                min="0"
              />
              <small className="form-text text-muted">
                Auto-calculated from line items with prices, or enter manually
              </small>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <div>
            <button className="btn btn-secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </button>
            {isEditing && (
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isSaving}
              >
                {isSaving ? 'Deleting...' : 'Delete Purchase Order'}
              </button>
            )}
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : isEditing ? 'Update Purchase Order' : 'Create Purchase Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderForm;
